/**
 * Transport options for a *versioned* read — one whose writes are guarded by a
 * request gate (directly, or by returning its value from `useAsyncRequest`'s
 * `fn` and rendering `data`) so a superseded response cannot commit.
 *
 * The gate alone is not enough, and the two missing halves are transport policy
 * rather than component state (#2821). Both are needed, and neither implies the
 * other:
 *
 * - `queryDeduplication: false` — Apollo answers a re-issued identical query
 *   from the request already in flight, so a read re-issued *after* a write is
 *   served by a request the server saw *before* it. Both callers share that one
 *   response, and the gate is left with nothing to choose between.
 * - `fetchPolicy: "no-cache"` — with deduplication off there really are two
 *   requests, and under any cache-writing policy each writes `InMemoryCache` on
 *   arrival. The older response then overwrites the newer normalized entities
 *   and broadcasts stale state to any cache observer or later `cache-first`
 *   consumer, whichever response the gate accepted.
 *
 * `no-cache` on its own does *not* opt a read out of deduplication. The
 * measured behaviour of each combination is pinned in
 * `core/async/apolloReentrancy.spec.ts`.
 *
 * ## The gate is a precondition, not a companion improvement (#3125)
 *
 * Do not apply this to an ungated read on the reasoning that it is "better than
 * nothing". It is not: deduplication and the opt-out are wrong in *different*
 * ways, and only one of them is bounded.
 *
 * Under deduplication the two invocations share one response, so they always
 * end the same way — both succeed or both fail. Opting out gives them
 * independent outcomes, and that is a capability the consumers never had to
 * cope with before: the newer read can succeed while the older one fails
 * afterwards. Every write a superseded invocation makes is then live, and the
 * failure writes are the ones that bite, because `catch` and `finally` blocks
 * are where gates are usually missing even when the success path has one.
 *
 * So the read needs a gate over **success, failure and `finally`**, and so does
 * every consumer that publishes shared state from the value — including callers
 * further up, which is why `meetingStore.fetchMeeting` swallows a superseded
 * failure entirely rather than throwing it at each of its callers. A read whose
 * consumers cannot be gated (a `useQuery`, whose `result` and `error` refs
 * belong to Apollo) cannot take this policy at all until it becomes an
 * imperative gated read.
 *
 * Spread into the query options, so a read cannot pick up one half:
 *
 * ```ts
 * const { data, errors } = await apolloClient.query({
 *   query: GetCotEventsDocument,
 *   variables: { cotId },
 *   ...VERSIONED_READ_POLICY,
 * });
 * ```
 */
export const VERSIONED_READ_POLICY = {
  fetchPolicy: "no-cache",
  context: { queryDeduplication: false },
} as const;

/**
 * `VERSIONED_READ_POLICY` for a read that carries its own Apollo context too —
 * an abort signal, say.
 *
 * Merging by hand is the one way left to pick up half the policy: a `context:`
 * key written after the spread silently replaces the deduplication opt-out
 * rather than adding to it, and nothing fails until two invocations overlap.
 *
 * ```ts
 * const { data } = await apolloClient.query({
 *   query: GetMembershipFeesPreviewDocument,
 *   variables: { clubId },
 *   ...versionedReadPolicyWithContext(apolloAbortContext(signal)),
 * });
 * ```
 */
export function versionedReadPolicyWithContext(context: Record<string, unknown> | undefined) {
  return {
    fetchPolicy: VERSIONED_READ_POLICY.fetchPolicy,
    context: { ...context, ...VERSIONED_READ_POLICY.context },
  } as const;
}
